import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { FloristProfile } from "@/types/florist";
import { useAuth } from "@/contexts/AuthContext";

export function useFloristProfile(floristId: string | undefined) {
  const queryClient = useQueryClient();
  const { user, isFlorist } = useAuth();

  const { data: floristProfile, isLoading, error } = useQuery({
    queryKey: ["floristProfile", floristId],
    queryFn: async () => {
      if (!floristId) throw new Error("Florist ID is required");

      const { data, error } = await supabase
        .from("store_profiles")
        .select("*")
        .eq("id", floristId)
        .single();

      if (error) throw error;
      if (!data) throw new Error("Profile not found");

      // Transform the data to match our frontend types
      const profile: FloristProfile = {
        id: data.id,
        store_name: data.store_name,
        store_phone: data.store_phone || "",
        store_email: data.store_email || "",
        street_address: data.street_address || "",
        suburb: data.suburb || "",
        state: data.state || "",
        postcode: data.postcode || "",
        about_text: data.about_text || "",
        contact_name: data.contact_name || "",
        personal_phone: data.personal_phone || "",
        business_phone: data.business_phone || "",
        contact_email: data.contact_email || user?.email || "",
        admin_email: data.admin_email || "",
        operating_hours: data.operating_hours || {},
        delivery_cutoff: data.delivery_cutoff || "",
        is_premium: data.is_premium || false,
        premium_since: data.premium_since || null,
        verified: data.verified || false,
        commission_rate: data.commission_rate || 0,
        created_at: data.created_at,
        updated_at: data.updated_at,
        delivery_start_time: data.delivery_start_time || "",
        delivery_end_time: data.delivery_end_time || "",
        delivery_slot_duration: data.delivery_slot_duration || "",
        logo_url: data.logo_url || "",
        banner_url: data.banner_url || "",
        social_links: data.social_links || {},
        delivery_fee: data.delivery_fee || 0,
        delivery_radius: data.delivery_radius || 5,
        minimum_order_amount: data.minimum_order_amount || 0,
        setup_progress: data.setup_progress || 0,
        store_status: data.store_status || "private",
        setup_completed: data.setup_completed || false,
        setup_completed_at: data.setup_completed_at || null,
        delivery_days: data.delivery_days || [],
        pickup_only_days: data.pickup_only_days || [],
        delivery_cutoff_times: data.delivery_cutoff_times || {},
        same_day_enabled: data.same_day_enabled || false,
        delivery_time_frames: data.delivery_time_frames || {
          morning: false,
          midday: false,
          afternoon: false,
        },
        coordinates: data.coordinates || "",
        geocoded_address: data.geocoded_address || {
          street_number: "",
          route: "",
          locality: "",
          administrative_area_level_1: "",
          postal_code: "",
          formatted_address: "",
          lat: 0,
          lng: 0,
        },
        delivery_distance_km: data.delivery_distance_km || null,
      };

      return profile;
    },
    enabled: !!floristId && isFlorist,
    staleTime: 1000 * 60, // Cache for 1 minute
  });

  const updateProfile = useMutation({
    mutationFn: async (updatedProfile: Partial<FloristProfile>) => {
      if (!floristId) throw new Error("Florist ID is required");
      if (!isFlorist) throw new Error("User must be a florist");

      const { error } = await supabase
        .from("store_profiles")
        .update(updatedProfile)
        .eq("id", floristId);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ["floristProfile", floristId] });
    },
  });

  return {
    floristProfile,
    isLoading,
    error,
    updateProfile,
  };
}